import { Heading, Text, VStack } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { FC, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

const LOG_PREFIX = 'SidePaneTabErrorBoundary';

interface SidePaneTabErrorBoundaryProps {
  children: ReactNode;
  tabName: string;
}

interface ErrorFallbackProps {
  tabName: string;
}

const SidePaneTabErrorFallback: FC<ErrorFallbackProps> = ({ tabName }) => {
  return (
    <VStack
      h="100%"
      p="2rem"
      justifyContent="center"
      textAlign="center"
      backgroundColor="#ECF3EE"
    >
      <Heading mb="1rem" as="h3" size="lg">
        Something went wrong
      </Heading>
      <Text mb="1rem" fontSize="md">
        Unable to load the {tabName} tab. Please refresh the page and try again.
      </Text>
      <Text fontSize="sm" color="text.muted">
        If this error persists, please reach out for support at
        help@berrystreet.co
      </Text>
    </VStack>
  );
};

export const SidePaneTabErrorBoundary: FC<SidePaneTabErrorBoundaryProps> = ({
  children,
  tabName,
}) => {
  const handleError = () => {
    const telemetry = { source: 'sidepane-tab-error-boundary', tabName };

    datadogRum.addAction(`${LOG_PREFIX} caught an error - ${tabName} tab`, {
      ...telemetry,
    });

    datadogRum.addError(new Error('Side pane tab render failure'), telemetry);

    const logMessage = `${LOG_PREFIX} - ${tabName} tab crashed. Displaying error message to user.`;
    datadogLogs.logger.error(logMessage, telemetry);

    if (process.env.NODE_ENV === 'development') {
      console.error(logMessage);
    }
  };

  return (
    <ErrorBoundary
      onError={handleError}
      fallback={<SidePaneTabErrorFallback tabName={tabName} />}
    >
      {children}
    </ErrorBoundary>
  );
};
